import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	useChatContextAIFeatureContext,
	useChatContextDatabaseContext,
	useChatContextEditorContext,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { useChatModeStoreActions } from '../../../../controllers/chat-mode-store/useChatModeStoreActions';

import { PageContextType } from './PageContextType';
import type { JiraViewContextData, CurrentPageContextType } from './types';
import { usePageContextType } from './usePageContextType';

const JIRA_VIEW_CONTEXT_KEY = 'jira_view_context';

function hasValidJiraViewContextData(value: unknown): value is JiraViewContextData {
	if (value == null || typeof value !== 'object') {
		return false;
	}
	const selected = Object.getOwnPropertyDescriptor(value, 'selectedIssueIdentifiers')?.value;
	return Array.isArray(selected);
}

export const useCurrentPageAsContext = (): {
	canUseCurrentPageContextUserPreference: (key: string) => boolean;
	setUseCurrentPageContextUserPreference: (key: string, isEnabled: boolean) => void;
	currentContext: CurrentPageContextType;
} => {
	const pageContextType = usePageContextType();
	const { getChatMode, setUseCurrentPageContext } = useChatModeStoreActions();
	const [editorContext] = useChatContextEditorContext();
	const [databaseContext] = useChatContextDatabaseContext();
	const [aiFeatureContext] = useChatContextAIFeatureContext();
	const rawJiraViewContext = aiFeatureContext?.[JIRA_VIEW_CONTEXT_KEY];
	const jiraViewContextData = hasValidJiraViewContextData(rawJiraViewContext)
		? rawJiraViewContext
		: undefined;

	let currentContext: CurrentPageContextType = {
		type: 'none',
	};

	if (editorContext) {
		// prefer editor context over page context if available
		currentContext = {
			type: 'editor_context',
			payload: editorContext,
		};
	} else if (databaseContext) {
		currentContext = {
			type: 'database_context',
			selectedElementIds: databaseContext.selectedElementIds ?? [],
		};
	} else if (
		jiraViewContextData &&
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		FeatureGates.getExperimentValue(
			'jira-ai-jira-view-context-in-rovo-chat-experiment',
			'isEnabled',
			false,
		)
	) {
		currentContext = {
			type: 'jira_view_context',
			payload: jiraViewContextData,
		};
	} else if (pageContextType !== PageContextType.UNKNOWN) {
		// if no editor context, use page context if available
		currentContext = {
			type: 'current_page',
			pageContextType,
		};
	}

	return {
		canUseCurrentPageContextUserPreference: (key: string): boolean => {
			return getChatMode(key).useCurrentPageContext;
		},
		setUseCurrentPageContextUserPreference: setUseCurrentPageContext,
		currentContext,
	};
};
